import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import type { BackendTypes } from '@tf/api';
import { FormBuilder } from '@tf/form-builder';
import {
	Box,
	Button,
	createModal,
	createStyles,
	EntityBadge,
	Flex,
	Icon,
	ModalFooter,
	ScrollArea,
	Select,
	Stack,
	TFListCard,
	TFNotifier,
	TFText,
	UnstyledButton,
	useModalControl,
} from '@tf/ui';
import type { ConnectionErrorResponse, SubmitHandler } from '@tf/utils';
import { connectionErrorsMap, S } from '@tf/utils';
import type { FC } from 'react';
import { useState } from 'react';
import { useGetEntityLabel } from '@/components/shared';
import { useConnectExistingMutation, useConnectNewMutation } from '@/core/api';
import { useAppEnv, useDefinitions } from '@/core/providers';

import { useExistingEntities } from './useExistingEntities';

const EXCLUDED_CONNECTIONS = ['CO_APPLICANT', 'NOMINATED_ACCOUNT', 'APPLICATION'];

export interface AddConnectionModalProps {
	parentEntityName: string;
	parentEntityId: BackendTypes.GraphId;
	parentEntityKind: BackendTypes.EntityKind;
	connectionKind: BackendTypes.EntityConnectionKind;
	onConnectionAdded?: (id: number) => void;
	withSuccessMessage?: boolean;
}

export const AddConnectionModal = createModal<AddConnectionModalProps>(
	({ parentEntityId, parentEntityKind, connectionKind, onConnectionAdded, withSuccessMessage = true }) => {
		const definitions = useDefinitions();
		const entityDef = definitions.defs.entities[parentEntityKind];
		const connectionDef = entityDef.possibleConnections.find((c) => c.kind === connectionKind);
		const availableEntities = connectionDef?.fromEntities.map((e) => e.kind) || [];
		const existingEntities = useExistingEntities(parentEntityId, availableEntities);

		return (
			<Box>
				{!EXCLUDED_CONNECTIONS.includes(connectionKind) && existingEntities.length > 0 ? (
					<CreateFromExisting
						parentEntityId={parentEntityId}
						connectionKind={connectionKind}
						availableEntities={availableEntities}
						onConnectionAdded={onConnectionAdded}
						withSuccessMessage={withSuccessMessage}
					/>
				) : (
					<CreateFromScratch
						parentEntityId={parentEntityId}
						connectionKind={connectionKind}
						availableEntities={availableEntities}
						onConnectionAdded={onConnectionAdded}
						withSuccessMessage={withSuccessMessage}
					/>
				)}
			</Box>
		);
	},
);

interface CreateFromProps {
	availableEntities: string[];
	parentEntityId: BackendTypes.GraphId;
	connectionKind: BackendTypes.EntityConnectionKind;
	onConnectionAdded?: (id: number) => void;
	withSuccessMessage: boolean;
}

export const CreateFromScratch: FC<CreateFromProps> = ({
	availableEntities,
	parentEntityId,
	connectionKind,
	onConnectionAdded,
	withSuccessMessage,
}) => {
	const { _ } = useLingui();
	const { onClose } = useModalControl();

	const [entityKind, setEntityKind] = useState<string>(availableEntities[0]);
	const appEnv = useAppEnv();
	const definitions = useDefinitions();
	const selfSegmentKind = definitions.defs.selfSegments[entityKind];
	const getEntityLabel = useGetEntityLabel();

	const onConnectionSuccess = (res: BackendTypes.EntityConnectResult) => {
		if (withSuccessMessage) {
			TFNotifier.success(
				_(
					msg({
						id: 'addConnectionModal.notifications.success',
						message: 'Connection added successfully',
					}),
				),
			);
		}
		onConnectionAdded?.(res.connectedEntityId);
		onClose();
	};

	const onConnectionError = () => {
		TFNotifier.error(
			_(
				msg({
					id: 'addConnectionModal.notifications.fail',
					message: 'Failed to add connection',
				}),
			),
		);
	};

	const connectNewMutation = useConnectNewMutation();
	const connectNew: SubmitHandler = async ({ values }) => {
		await connectNewMutation
			.mutateAsync({
				parentEntityId,
				connectionKind,
				entityKind,
				selfSegmentData: values[selfSegmentKind],
			})
			.then(onConnectionSuccess)
			.catch(onConnectionError);
	};

	return (
		<>
			{availableEntities.length > 1 ? (
				<Select
					label={_(msg({ id: 'addConnectionModal.form.entityKindLabel', message: 'Entity type' }))}
					value={entityKind}
					onChange={(value) => {
						if (value) {
							setEntityKind(value);
						}
					}}
					placeholder={_(
						msg({
							id: 'addConnectionModal.form.entityKindPlaceholder',
							message: 'Select a kind of entity',
						}),
					)}
					data={availableEntities.map((kind) => {
						const entityLabel = getEntityLabel(kind);
						return { value: kind, label: entityLabel || S.prettify(kind) };
					})}
					m=".25rem .75rem 0"
				/>
			) : null}
			<FormBuilder
				currency={definitions.currency}
				segments={[
					{
						segmentKind: selfSegmentKind,
						graphId: 0,
						accessMode: 'MODIFY',
					},
				]}
				shouldValidate
				apiConfig={{
					username: '',
					clusterId: appEnv.clusterId,
				}}
				processingScripts={definitions.scripts}
				defs={{
					structs: definitions.defs.structs,
					options: definitions.defs.options,
				}}
				styles={{ content: { padding: '.5rem .75rem .25rem' } }}
				submitButtonRenderer={(isLoading) => {
					return (
						<ModalFooter
							isLoading={isLoading}
							submitLabel={_(
								msg({
									id: 'addConnectionModal.addButton',
									message: 'Add',
								}),
							)}
						/>
					);
				}}
				onSubmit={connectNew}
			/>
		</>
	);
};

const useStyles = createStyles(({ colors }) => ({
	title: {
		color: colors.gray[9],
		fontWeight: 500,
		fontSize: 13.2,
		lineHeight: 'normal',
	},
	subtitle: {
		marginTop: '4px',
		color: colors.gray[4],
		fontWeight: 500,
		fontSize: 13,
		lineHeight: 'normal',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
}));

type ConnectionType = 'new' | 'existing';

const CreateFromExisting: FC<CreateFromProps> = ({
	parentEntityId,
	availableEntities,
	connectionKind,
	onConnectionAdded,
	withSuccessMessage = true,
}) => {
	const { _ } = useLingui();
	const { onClose } = useModalControl();
	const existingEntities = useExistingEntities(parentEntityId, availableEntities);
	const [selectedExistingEntity, setSelectedExistingEntity] = useState<BackendTypes.Entity>();
	const [connectionType, setConnectionType] = useState<ConnectionType>('existing');

	const { classes } = useStyles();

	const getEntityLabel = useGetEntityLabel();

	const onConnectionSuccess = (entityId: BackendTypes.GraphId) => {
		if (withSuccessMessage) {
			TFNotifier.success(
				_(
					msg({
						id: 'addConnectionModal.notifications.success',
						message: 'Connection added successfully',
					}),
				),
			);
		}
		onConnectionAdded?.(entityId);
		onClose();
	};

	const onConnectionError = () => {
		TFNotifier.error(
			_(
				msg({
					id: 'addConnectionModal.notifications.fail',
					message: 'Failed to add connection',
				}),
			),
		);
	};

	const onExistingConnectionError = async (error: Error) => {
		if (error instanceof Response) {
			try {
				const errorResponse: ConnectionErrorResponse = await error.json();
				// TODO: why we handle this in so special way
				if (errorResponse.message === 'failed on db rules check') {
					TFNotifier.error(
						_(
							msg({
								id: 'addConnectionModal.notifications.fail',
								message: 'Failed to add connection',
							}),
						),
					);
					return;
				}

				for (const [key, value] of Object.entries(errorResponse)) {
					if (value.length) {
						TFNotifier.error(connectionErrorsMap[key]);
						return;
					}
				}
			} catch (e) {
				console.log('Failed to parse error', e);
			}
		} else {
			onConnectionError();
		}
	};

	const connectExistingMutation = useConnectExistingMutation();
	const connectExisting = async () => {
		if (!selectedExistingEntity) {
			return;
		}
		await connectExistingMutation
			.mutateAsync({
				parentEntityId,
				connectionKind,
				entityId: selectedExistingEntity.graphNodeId,
			})
			.then(() => onConnectionSuccess(selectedExistingEntity.graphNodeId))
			.catch(onExistingConnectionError);
	};

	if (connectionType === 'new') {
		return (
			<>
				<UnstyledButton
					sx={({ colors }) => ({
						color: colors.brand[6],
						fill: colors.brand[6],
						fontWeight: 600,
						fontSize: 14,
						'&:hover': {
							color: colors.brand[8],
							fill: colors.brand[8],
						},
					})}
					m="16px 0 12px 8px"
					onClick={() => setConnectionType('existing')}
				>
					<Flex align="center">
						<Icon.IconChevronLeft size={26} />
						<TFText>
							<Trans id="addConnectionModal.backToSearchButton">Back to search results</Trans>
						</TFText>
					</Flex>
				</UnstyledButton>
				<CreateFromScratch
					parentEntityId={parentEntityId}
					connectionKind={connectionKind}
					availableEntities={availableEntities}
					onConnectionAdded={onConnectionAdded}
					withSuccessMessage={withSuccessMessage}
				/>
			</>
		);
	}

	return (
		<>
			<Stack m=".75rem .75rem .3rem">
				<ScrollArea h={existingEntities.length > 10 ? '500px' : 'auto'}>
					<Stack gap="8px">
						{existingEntities.map((entity) => (
							<TFListCard
								onClick={() => setSelectedExistingEntity(entity)}
								isActive={selectedExistingEntity?.graphNodeId === entity.graphNodeId}
								key={entity.name}
								type="radio"
							>
								<Flex align="center" ml="12px" w="100%" justify="space-between">
									<Box mr="12px" maw="280px">
										<TFText className={classes.title}>{entity.name}</TFText>
									</Box>
									<EntityBadge kind={getEntityLabel(entity.entityKind)} />
								</Flex>
							</TFListCard>
						))}
					</Stack>
				</ScrollArea>
				<Button
					size="lg"
					style={{ borderRadius: '4px' }}
					leftSection={<Icon.IconPlus size="20px" color={'#155EEF'} />}
					variant="default"
					onClick={() => setConnectionType('new')}
				>
					<TFText className={classes.title}>
						<Trans id="addConnectionModal.createNewButton">Create new</Trans>
					</TFText>
				</Button>
			</Stack>
			<ModalFooter
				isLoading={connectExistingMutation.isPending}
				isDisabled={connectExistingMutation.isPending || !selectedExistingEntity}
				submitLabel={_(
					msg({
						id: 'addConnectionModal.addButton',
						message: 'Add',
					}),
				)}
				onSubmit={connectExisting}
			/>
		</>
	);
};
